import React from 'react';
import { Plus, Edit, Trash2, DollarSign } from 'lucide-react';
import { formatCurrency } from '../../../shared/utils';
import type { CategorySummary } from '../../../shared/types/budget';

interface BudgetCategoryListProps {
  categories: CategorySummary[];
  onEditCategory?: (categoryId: string) => void;
  onDeleteCategory?: (categoryId: string) => void;
  onAddBudgetItem?: (categoryId: string) => void;
  className?: string;
}

export const BudgetCategoryList: React.FC<BudgetCategoryListProps> = ({
  categories,
  onEditCategory,
  onDeleteCategory,
  onAddBudgetItem,
  className = ''
}) => {
  const getStatusColor = (status: CategorySummary['status']) => {
    switch (status) {
      case 'over_budget':
        return 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20';
      case 'under_budget':
        return 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20';
      default:
        return 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20';
    }
  };

  const getStatusText = (status: CategorySummary['status']) => {
    switch (status) {
      case 'over_budget':
        return 'Over Budget';
      case 'under_budget':
        return 'Under Budget';
      default:
        return 'On Track';
    }
  };

  if (categories.length === 0) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center ${className}`}>
        <DollarSign className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Budget Categories</h3>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          Start by creating budget categories for your campaign.
        </p>
        <button 
          onClick={() => onAddBudgetItem?.('')}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Budget Category
        </button>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {categories.map((category) => (
        <div
          key={category.category_id}
          className={`border rounded-lg p-4 transition-all duration-200 ${getStatusColor(category.status)}`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <div 
                className="w-4 h-4 rounded-full mr-3"
                style={{ backgroundColor: category.category_color }}
              />
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  {category.category_name}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {getStatusText(category.status)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {onEditCategory && (
                <button
                  onClick={() => onEditCategory(category.category_id)}
                  className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  <Edit className="h-4 w-4" />
                </button>
              )}
              {onDeleteCategory && (
                <button
                  onClick={() => onDeleteCategory(category.category_id)}
                  className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              {onAddBudgetItem && (
                <button
                  onClick={() => onAddBudgetItem(category.category_id)}
                  className="p-1 text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Budget Details */}
          <div className="grid grid-cols-3 gap-4 mb-3">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Allocated</p>
              <p className="font-semibold text-gray-900 dark:text-white">
                {formatCurrency(category.allocated)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Spent</p>
              <p className="font-semibold text-gray-900 dark:text-white">
                {formatCurrency(category.spent)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Remaining</p>
              <p className={`font-semibold ${
                category.remaining < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
              }`}>
                {formatCurrency(category.remaining)}
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">Usage</span>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                {category.percentage_used.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div 
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  category.status === 'over_budget' ? 'bg-red-500' :
                  category.status === 'under_budget' ? 'bg-amber-500' :
                  'bg-green-500'
                }`}
                style={{ width: `${Math.min(category.percentage_used, 100)}%` }}
              />
            </div>
          </div>

          {/* Performance Score */}
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 dark:text-gray-400">
              Performance: {category.performance_score.toFixed(0)}%
            </span>
            <span className={`font-medium ${
              category.performance_score > 70 ? 'text-green-600 dark:text-green-400' :
              category.performance_score > 40 ? 'text-amber-600 dark:text-amber-400' :
              'text-red-600 dark:text-red-400'
            }`}>
              {category.performance_score > 70 ? 'Excellent' :
               category.performance_score > 40 ? 'Good' : 'Needs Attention'}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};